import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiService from '../utils/api';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Stack
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Flag as FlagIcon,
  EmailOutlined as EmailIcon,
  LockOutlined as LockIcon
} from '@mui/icons-material';
import useAuthStore from '../store/authStore';
import AuthMarketingPanel from '../components/AuthMarketingPanel';

const LANGUAGES = [
  { code: 'en', flag: '🇬🇧', label: 'EN' },
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'it', flag: '🇮🇹', label: 'IT' }
];

const LoginPage = () => {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const { t, i18n } = useTranslation();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await apiService.request('/auth/login', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      setAuth(data.user, data.token);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100dvh',
      bgcolor: 'var(--auth-bg)',
      display: 'flex',
      overflowY: 'auto',
      backgroundImage: 'radial-gradient(rgba(99,102,241,0.07) 1px, transparent 1px)',
      backgroundSize: '28px 28px'
    }}>

      {/* ── Left: Form ─────────────────────────────────────────── */}
      <Box sx={{
        width: { xs: '100%', md: '460px', lg: '520px' },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', md: 'center' },
        pt: { xs: 5, md: 0 },
        pb: { xs: 5, md: 0 },
        px: { xs: 2, md: 4 },
        position: 'relative',
        zIndex: 1
      }}>
        <Box sx={{ width: '100%', maxWidth: 400 }}>

        {/* Card */}
        <Box sx={{
          bgcolor: 'var(--auth-card)',
          border: '1px solid var(--line-1)',
          borderRadius: 2,
          p: { xs: 3, sm: 4 },
          position: 'relative',
          overflow: 'hidden',
          /* top accent */
          '&::before': {
            content: '""', position: 'absolute',
            top: 0, left: 0, right: 0, height: 2,
            background: 'linear-gradient(90deg,#6366f1,#3b82f6)'
          }
        }}>

          {/* Logo + title */}
          <Stack alignItems="center" spacing={1.25} sx={{ mb: 3.5 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 1.5,
              bgcolor: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.28)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <FlagIcon sx={{ color: '#6366f1', fontSize: 22 }} />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--fg-strong)', lineHeight: 1.2 }}>
                Roadmap Lab
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'var(--fg-faint)', mt: 0.4 }}>
                {t('auth.login.subtitle') || 'Sign in to your account'}
              </Typography>
            </Box>
          </Stack>

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 1.5, fontSize: '0.82rem' }}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                fullWidth size="small"
                label={t('auth.login.email') || 'Email'}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required autoFocus autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ fontSize: 17, color: 'var(--fg-faint)' }} />
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth size="small"
                label={t('auth.login.password') || 'Password'}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ fontSize: 17, color: 'var(--fg-faint)' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small"
                        sx={{ color: 'var(--fg-faint)' }}>
                        {showPassword ? <VisibilityOff sx={{ fontSize: 17 }} /> : <Visibility sx={{ fontSize: 17 }} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Button
                type="submit" variant="contained" fullWidth size="medium"
                disabled={loading}
                sx={{
                  mt: 0.5, py: 1.1, fontWeight: 700, fontSize: '0.88rem',
                  background: 'linear-gradient(135deg,#6366f1,#3b82f6)',
                  boxShadow: 'none', borderRadius: 1.5,
                  '&:hover': { boxShadow: '0 0 20px rgba(99,102,241,0.3)' },
                  '&:disabled': { opacity: 0.6 }
                }}
              >
                {loading
                  ? <CircularProgress size={18} sx={{ color: 'white' }} />
                  : (t('auth.login.signIn') || 'Sign In')}
              </Button>
            </Stack>

            {/* Sign up link */}
            <Box sx={{ textAlign: 'center', mt: 2.5 }}>
              <Typography sx={{ fontSize: '0.8rem', color: 'var(--fg-faint)' }}>
                {t('auth.login.noAccount') || "Don't have an account?"}{' '}
                <Link to="/signup" style={{ textDecoration: 'none' }}>
                  <Box component="span" sx={{ color: 'var(--accent-fg)', fontWeight: 600, '&:hover': { color: '#6366f1' } }}>
                    {t('auth.login.createAccount') || 'Create one'}
                  </Box>
                </Link>
              </Typography>
            </Box>
          </form>
        </Box>

        {/* Language switcher below card */}
        <Stack direction="row" justifyContent="center" spacing={0.75} sx={{ mt: 2 }}>
          {LANGUAGES.map((lang) => (
            <Box
              key={lang.code}
              onClick={() => i18n.changeLanguage(lang.code)}
              sx={{
                px: 1.5, py: 0.5, borderRadius: 1, cursor: 'pointer',
                fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s',
                color: i18n.language === lang.code ? 'var(--accent-fg)' : 'var(--fg-faint)',
                bgcolor: i18n.language === lang.code ? 'rgba(99,102,241,0.1)' : 'transparent',
                border: '1px solid ' + (i18n.language === lang.code ? 'rgba(99,102,241,0.25)' : 'transparent'),
                '&:hover': { color: 'var(--accent-fg)' }
              }}
            >
              {lang.flag} {lang.label}
            </Box>
          ))}
        </Stack>

        </Box>{/* end maxWidth wrapper */}
      </Box>{/* end left column */}

      {/* ── Right: Marketing panel ─────────────────────────────── */}
      <AuthMarketingPanel />

    </Box>
  );
};

export default LoginPage;
